import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../../api/client";

/**
 * Ciclo de vida del staff de plataforma — `superadmin/staff/` (TASK-21).
 * Solo PLATFORM_ADMIN: el resto de roles recibe 403 incluso en el GET.
 */
const STAFF_KEY = ["superadmin", "staff"];

/**
 * Lista de staff. El endpoint **NO pagina** (devuelve un array plano) y el
 * orden es fijo (`-date_joined`). Incluye a los desactivados a propósito, para
 * poder reactivarlos. El filtro `?is_active` existe pero se aplica en cliente:
 * la lista es el equipo interno (decenas de filas) y así una sola clave de
 * caché sirve para todas las vistas y basta una invalidación por mutación.
 */
function useStaffList(options = {}) {
  return useQuery({
    queryKey: STAFF_KEY,
    queryFn: () =>
      apiClient.get("/api/v1/superadmin/staff/").then((r) => r.data?.results ?? r.data),
    ...options,
  });
}

/** POST { email, name, roles[] } → 201. Crea la cuenta con clave temporal. */
function useInviteStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post("/api/v1/superadmin/staff/", data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAFF_KEY }),
  });
}

/**
 * PATCH { roles } — **reemplazo total** de la lista de roles, no un merge.
 * Enviar el mismo conjunto es un no-op y no genera fila de bitácora.
 */
function useUpdateStaffRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roles }) =>
      apiClient.patch(`/api/v1/superadmin/staff/${id}/`, { roles }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAFF_KEY }),
  });
}

/**
 * DELETE → soft-delete: responde **200 con el objeto completo** (no 204) y solo
 * pone `is_active=false`; nunca toca `is_staff` ni los grupos, así que es
 * reversible con `useReactivateStaff`. Puede fallar con 409
 * (`staff_self_deactivation`, `staff_last_platform_admin`).
 */
function useDeactivateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      apiClient.delete(`/api/v1/superadmin/staff/${id}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAFF_KEY }),
  });
}

/** PATCH { is_active: true }. Los roles sobreviven a la desactivación. */
function useReactivateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      apiClient
        .patch(`/api/v1/superadmin/staff/${id}/`, { is_active: true })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAFF_KEY }),
  });
}

export {
  STAFF_KEY,
  useStaffList,
  useInviteStaff,
  useUpdateStaffRoles,
  useDeactivateStaff,
  useReactivateStaff,
};
